import Swiper, { Navigation, Lazy, Keyboard, A11y } from 'swiper';
import { a11y } from './utils/slider';

import tplImageViewer from './templates/image_viewer.pug';

class ImageViewer {
  constructor(parentEl, options = {}) {
    const _defaults = {
      selector: '[data-image-viewer]',
      extrasAttr: 'data-image-viewer-extras',
      templateFn: tplImageViewer,
      templateData: null,
    };
    this.opts = { ..._defaults, ...options };
    
    this.parentEl = (typeof parentEl === 'string') ? document.querySelector(parentEl) : parentEl;
    this.linkEls = this.parentEl.querySelectorAll(this.opts.selector);
    
    this.triggerEl = null;
    this.viewerEl = null;
    this.viewerBackdropEl = null;
    this.btnCloseEl = null;
    this.hasTransition = false;
    this.transitionDuration = 0;
    this.sliderEl = null;
    this.slider = null;

    this.contents = this._getContents();

    this.boundClick = e => this._onClick(e);
    this.boundClickClose = e => this._onClickClose(e);
    this.boundKeyDown = e => this._onKeyDown(e);

    this.init();
  }

  init() {
    Array.from(this.linkEls).forEach(linkEl => {
      linkEl.addEventListener('click', this.boundClick, false);
    });
  }

  destroy() {
    Array.from(this.linkEls).forEach(linkEl => {
      linkEl.removeEventListener('click', this.boundClick);
    });
  }

  _onClick(e) {
    e.preventDefault();
    this.triggerEl = e.currentTarget;
    const index = this._getIndex(e.currentTarget);
    this._openViewer(index);
  }

  _onClickClose(e) {
    e.preventDefault();
    if (this.slider) {
      this._closeViewer();
    }
  }

  _onKeyDown(e) {
    if (e.key.toLowerCase() === 'escape') {
      this._closeViewer();
    }
  }

  _openViewer(index) {
    if (typeof this.opts.templateFn !== 'function') {
      return;
    }
    const html = this.opts.templateFn({
      contents: this.contents,
      i18n: {
        btn_close: (typeof Settings.general.i18n.btn_close !== 'undefined') ? Settings.general.i18n.btn_close : 'Bezárás',
        loading_label: (typeof Settings.general.i18n.loading_label !== 'undefined') ? Settings.general.i18n.loading_label : 'Betöltés...',
      },
      templateData: this.opts.templateData,
    });
    document.body.insertAdjacentHTML('beforeend', html);

    this.viewerEl = document.querySelector('.image-viewer');
    this.viewerBackdropEl = this.viewerEl.querySelector('.image-viewer-backdrop');
    this.btnCloseEl = this.viewerEl.querySelector('[data-image-viewer-close]');
    this.sliderEl = this.viewerEl.querySelector('.swiper');

    // check for viewer transition effect:
    if (this.viewerEl.classList.contains('transition')) {
      this.hasTransition = true;
      if (getComputedStyle(this.viewerEl).getPropertyValue('--image-viewer-transition-speed')) {
        this.transitionDuration = parseInt(getComputedStyle(this.viewerEl).getPropertyValue('--image-viewer-transition-speed').match(/[0-9]+/).toString() || 0);
      }
      setTimeout(() => {
        this.viewerEl.classList.add('transition-active');
      }, 10);
    }

    // set keyboard event listener
    document.addEventListener('keydown', this.boundKeyDown, false);

    // set close click event listeners
    if (this.viewerBackdropEl) {
      this.viewerBackdropEl.addEventListener('click', this.boundClickClose, false);
    }
    if (this.btnCloseEl) {
      this.btnCloseEl.addEventListener('click', this.boundClickClose, false);
    }

    // autofocus navigation next button:
    if (this.sliderEl.querySelector('[data-slider-nav="next"]')) {
      this.sliderEl.querySelector('[data-slider-nav="next"]').focus();
    }

    // disable body scroll
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = '10px';

    // init swiper
    this.slider = new Swiper(this.sliderEl, {
      modules: [Navigation, Lazy, Keyboard, A11y],
      slidesPerView: 1,
      spaceBetween: 0,
      watchSlidesProgress: true,
      lazy: true,
      navigation: {
        prevEl: this.sliderEl.querySelector('[data-slider-nav="prev"]'),
        nextEl: this.sliderEl.querySelector('[data-slider-nav="next"]'),
      },
      keyboard: true,
      a11y: a11y(),
    });

    // jump to index
    this.slider.slideTo(index, 0);
  }

  _closeViewer() {
    if (!this.viewerEl) {
      return;
    }

    if (this.hasTransition) {
      this.viewerEl.classList.remove('transition-active');
    }

    setTimeout(() => {
      if (this.slider) {
        this.slider.destroy(true, true);
      }
      this.viewerEl.parentNode.removeChild(this.viewerEl);
      if (this.triggerEl) {
        this.triggerEl.focus();
      }
      if (this.viewerBackdropEl) {
        this.viewerBackdropEl.removeEventListener('click', this.boundClickClose);
      }
      if (this.btnCloseEl) {
        this.btnCloseEl.removeEventListener('click', this.boundClickClose);
      }
      document.removeEventListener('keydown', this.boundKeyDown);

      // enable body scroll
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }, this.transitionDuration);
  }

  _getContents() {
    const result = [];
    Array.from(this.parentEl.querySelectorAll(this.opts.selector)).forEach(linkEl => {
      const alt = (linkEl.querySelector('img') && linkEl.querySelector('img').hasAttribute('alt')) ? linkEl.querySelector('img').getAttribute('alt') : null;
      const title = linkEl.getAttribute('title') || null;
      const extras = (linkEl.hasAttribute(this.opts.extrasAttr)) ? this._getExtras(linkEl.getAttribute(this.opts.extrasAttr)) : null;
      const imgEl = linkEl.querySelector('img');
      const width = (imgEl.hasAttribute('width')) ? parseInt(imgEl.getAttribute('width')) : '';
      const height = (imgEl.hasAttribute('height')) ? parseInt(imgEl.getAttribute('height')) : '';
      result.push({
        src: linkEl.getAttribute('href'),
        width,
        height,
        alt,
        title,
        extras,
      });
    });
    return result;
  }

  _getExtras(queryString) {
    const result = {};
    const params = new URLSearchParams(queryString);
    for(const [key, value] of params) {
      result[key] = value;
    }
    return result;
  }

  _getIndex(linkEl) {
    return Array.from(this.linkEls).findIndex(el => el === linkEl);
  }
};

export default ImageViewer;
